import React, { FC, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import {
  Modal,
  Button,
  Quantity,
  Alert,
  Progress,
  Skeleton,
} from "../../../components";
import { useStore } from "effector-react";
import { $selectedPersonTypes } from "../../../store/selected/selectedPersonTypes";
import { PersonTypesCard, AvailabilityCard } from "../../../cards";
import cloneDeep from "lodash/cloneDeep";
import { useTranslation } from "react-i18next";
import {
  $selectedActivities,
  selectedActivitiesApi,
} from "../../../store/selected/selectedActivities";
import {
  $selectedDates,
  $selectedPreferredDays,
} from "../../../store/selected/selectedDateRange";
import { $selectedLocation } from "../../../store/selected/selectedLocations";
import { ActivitiesEndpoint } from "../../../store/endpoints/activities";
import {
  Activity,
  ActivityAvailability,
  PersonType,
} from "../../../store/models";
import { AppConfig } from "../../../config";
import { BsClock, BsPencil } from "react-icons/bs";

export const editActivity = (activity: Activity): void => {
  const container = document.createElement("div");
  document.body.appendChild(container);

  const unmount = () => {
    const unmountResult = ReactDOM.unmountComponentAtNode(container);
    if (unmountResult && container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  setTimeout(() => {
    ReactDOM.render(
      <EditActivityModal activity={activity} onClose={unmount} />,
      container
    );
  });
};

interface Props {
  activity: Activity;
  onClose: () => void;
}
const EditActivityModal: FC<Props> = ({ onClose, activity }) => {
  const { t } = useTranslation(AppConfig.translationFileNames.step_Main);
  // START Subscriptions to outer data
  const selectedDates = useStore($selectedDates);
  const selectedDatesFixedTimeSlotIncrementNumber =
    activity.reservation_time_slot_increment;
  const selectedPreferredDays = useStore($selectedPreferredDays);
  const selectedLocation = useStore($selectedLocation);
  const selectedActivities = useStore($selectedActivities);
  const selectedPersonTypes = useStore($selectedPersonTypes);
  const { data: availabilitySlotsReceived, pending } = useStore(
    ActivitiesEndpoint.timeSlots.store
  );
  const { pending: reservePending, error: reserveError } = useStore(
    ActivitiesEndpoint.reserve.store
  );
  const { pending: cancelReservationPending } = useStore(
    ActivitiesEndpoint.cancelReservation.store
  );
  // END Subscriptions to outer data

  // START Local data stores
  const [personTypes, setPersonTypes] = useState<
    (PersonType & { quantity: number })[]
  >([]);
  const [duration, setDuration] = useState<number>(1); // in minutes
  const [selectedActivity, setSelectedActivity] = useState<
    (typeof selectedActivities)[0] | null
  >(null);
  const [availabilitySlot, setAvailabilitySlot] =
    useState<ActivityAvailability | null>(null);
  const isAvailabilitySelected = !!availabilitySlot;
  const [availabilitySlots, setAvailabilitySlots] = useState<
    ActivityAvailability[]
  >([]);
  // END Local data stores

  useEffect(() => {
    if (availabilitySlotsReceived) {
      setAvailabilitySlots(availabilitySlotsReceived);
      if (availabilitySlotsReceived.length === 1) {
        setAvailabilitySlot(availabilitySlotsReceived[0]);
      }
    }
  }, [availabilitySlotsReceived]);

  const resetAvailability = () => {
    ActivitiesEndpoint.timeSlots.reset();
    setAvailabilitySlots([]);
    setAvailabilitySlot(null);
  };

  const onPersonTypeChange = (v: { id: number; qty: number; name: string }) => {
    const storeCloned = cloneDeep(personTypes);

    const item = storeCloned.find((i) => i.id === v.id);
    if (item) item.quantity = v.qty;

    setPersonTypes(storeCloned);
    resetAvailability();
  };

  const onDurationChange = (v: number) => {
    setDuration(v);
    resetAvailability();
  };

  useEffect(() => {
    const selectedActivity = selectedActivities.find(
      ({ entity: { id } }) => id === activity.id
    );
    if (selectedActivity) {
      const mergedPersonTypes = [...selectedPersonTypes].map((item) => {
        const selectedPersonType = selectedActivity.personTypes.find(
          ({ id }) => id === item.id
        );
        return {
          ...item,
          quantity: selectedPersonType ? selectedPersonType.quantity : 0,
        };
      });
      setSelectedActivity(selectedActivity);

      setPersonTypes(mergedPersonTypes);
      setDuration(
        selectedActivity.timeSlots *
          selectedDatesFixedTimeSlotIncrementNumber *
          activity.slot_duration
      );
      setAvailabilitySlot(selectedActivity.availabilitySlot);
      setAvailabilitySlots(selectedActivity.availabilitySlots);
    } else {
      setPersonTypes(cloneDeep(selectedPersonTypes));
    }
    return () => {
      ActivitiesEndpoint.timeSlots.reset();
      ActivitiesEndpoint.reserve.reset();
      ActivitiesEndpoint.cancelReservation.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = async (): Promise<void> => {
    if (availabilitySlot) {
      if (selectedActivity) {
        await ActivitiesEndpoint.cancelReservation.call(
          selectedActivity.instancesIds
        );
      }

      const instancesIds = await ActivitiesEndpoint.reserve.call({
        activity: activity,
        personTypes: personTypes,
        availability: availabilitySlot,
      });

      selectedActivitiesApi.save({
        entity: activity,
        slotDuration: activity.slot_duration,
        personTypes: personTypes.filter(({ quantity }) => quantity > 0),
        timeSlots: duration / activity.slot_duration,
        availabilitySlot,
        availabilitySlots,
        instancesIds,
      });
      onClose();
    }
  };

  const searchAvailableTimeSlots = async () => {
    if (selectedDates && selectedLocation) {
      setAvailabilitySlot(null);
      setAvailabilitySlots([]);
      await ActivitiesEndpoint.timeSlots.call({
        id: activity.id,
        locationId: selectedLocation.id,
        dates: selectedDates,
        weekdays: selectedPreferredDays,
        personTypes: personTypes.filter(({ quantity }) => quantity > 0),
        timeSlots: duration / activity.slot_duration,
      });
    }
  };

  return (
    <Modal
      visible={true}
      onClose={onClose}
      getContainer={false}
      destroyOnClose={false}
      width={500}
    >
      {(reservePending || cancelReservationPending) && (
        <Progress fill style={{ borderRadius: 15 }}>
          {t("activityModal.reserving")}
        </Progress>
      )}

      <h2 style={{ marginTop: 0 }}>{t("activityModal.Details")}</h2>

      <div style={isAvailabilitySelected ? { opacity: 0.5 } : {}}>
        <h4 style={{ fontWeight: 500 }}>{t("activityModal.Duration")}</h4>
        <Quantity.Time
          disabled={isAvailabilitySelected}
          min={
            selectedDatesFixedTimeSlotIncrementNumber * activity.slot_duration
          }
          duration={
            selectedDatesFixedTimeSlotIncrementNumber * activity.slot_duration
          }
          value={duration}
          onChange={onDurationChange}
        />

        <h4 style={{ fontWeight: 500 }}>{t("activityModal.PersonTypes")}</h4>
        <div>
          <PersonTypesCard
            disabled={isAvailabilitySelected}
            style={{ marginBottom: 30 }}
            value={personTypes}
            onChange={onPersonTypeChange}
            min={1}
            maxValue={selectedPersonTypes}
            fullwidth
          />
        </div>
      </div>

      <div>
        {!isAvailabilitySelected ? (
          <Button
            icon={<BsClock />}
            // size={"small"}
            variant={"action"}
            color={"primary"}
            disabled={pending}
            onClick={searchAvailableTimeSlots}
          >
            {t("activityModal.checkAvailability")}
          </Button>
        ) : (
          <Button
            icon={<BsPencil />}
            // size={"small"}
            variant={"action"}
            color={"edit"}
            onClick={() => setAvailabilitySlot(null)}
          >
            {t("activityModal.edit")}
          </Button>
        )}
      </div>

      {(pending || availabilitySlots.length > 0) && (
        <h4 style={{ fontWeight: 500 }}>{t("activityModal.TimeSlots")}</h4>
      )}

      {pending && (
        <Skeleton height={150} showIcon>
          {t("activityModal.retrievingTimeSlots")}
        </Skeleton>
      )}

      {!pending && availabilitySlots.length > 0 && (
        <AvailabilityCard
          data={availabilitySlots}
          selected={availabilitySlot}
          onSelect={setAvailabilitySlot as any}
        />
      )}

      {!pending && availabilitySlotsReceived?.length === 0 && (
        <Alert variant={"error"} style={{ marginTop: 15 }}>
          {t("activityModal.noResults")}
        </Alert>
      )}

      {reserveError && (
        <Alert variant={"error"} style={{ marginTop: 15 }}>
          {t("activityModal.booked")}
        </Alert>
      )}

      {isAvailabilitySelected && (
        <Button
          style={{ marginTop: 20 }}
          variant={"primary"}
          onClick={apply}
          disabled={selectedActivity?.availabilitySlot === availabilitySlot}
        >
          {t("activityModal.apply")}
        </Button>
      )}
    </Modal>
  );
};
